import React, { useState, useEffect, useRef, useCallback } from 'react';
import ServiceNavigation from '../ServiceNavigation.jsx';
import BrowserCheck from '../BrowserCheck';
import TopPanel from '../TopPanel';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import SteeringDocViewer from './SteeringDocViewer';
import UrlInputPanel from './UrlInputPanel';
import PipelineStatus from './PipelineStatus';
import { sendAnalytics } from '../../resources/rum-provider';
import { STEERING_GENERATOR_ENDPOINT } from '../../resources/prod-env.jsx';
import { io } from 'socket.io-client';

import {
    AppLayout,
    SpaceBetween,
    Alert,
} from '@cloudscape-design/components';

import '../../styles/form.scss';
import './steering_generator.scss';

const demoModule = "SteeringGenerator";
const demoType   = "tool";

// localStorage key for the most recently submitted URL list. The pipeline
// runs to completion server-side even after the browser tab closes; the
// result lands in the cache. By restoring the URLs the user submitted
// earlier we let them come back, hit "Generate", and see the cached
// result instantly (no need to re-type anything).
const RECENT_URLS_KEY = 'steering-generator:recent-urls';

// localStorage key for the user's preferred char limit on generated docs.
// Backend default is 5000; we persist whatever the user last chose so it
// survives page reloads.
const CHAR_LIMIT_KEY     = 'steering-generator:char-limit';
const DEFAULT_CHAR_LIMIT = 5000;
const MIN_CHAR_LIMIT     = 500;
const MAX_CHAR_LIMIT     = 50000;

function loadRecentUrlEntries() {
    try {
        const raw = window.localStorage.getItem(RECENT_URLS_KEY);
        if (!raw) return null;
        const urls = JSON.parse(raw);
        if (!Array.isArray(urls) || !urls.length) return null;
        return urls.slice(0, 50).map((url, i) => ({ id: i + 1, url: String(url) }));
    } catch {
        return null;
    }
}

function saveRecentUrlEntries(entries) {
    try {
        const urls = entries.map(e => e.url.trim()).filter(Boolean);
        if (urls.length) {
            window.localStorage.setItem(RECENT_URLS_KEY, JSON.stringify(urls));
        } else {
            window.localStorage.removeItem(RECENT_URLS_KEY);
        }
    } catch {
        /* localStorage unavailable / quota — ignore */
    }
}

function loadCharLimit() {
    try {
        const raw = window.localStorage.getItem(CHAR_LIMIT_KEY);
        if (raw == null) return DEFAULT_CHAR_LIMIT;
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n)) return DEFAULT_CHAR_LIMIT;
        return Math.min(MAX_CHAR_LIMIT, Math.max(MIN_CHAR_LIMIT, n));
    } catch {
        return DEFAULT_CHAR_LIMIT;
    }
}

function saveCharLimit(value) {
    try {
        const n = parseInt(value, 10);
        if (!Number.isFinite(n) || n < MIN_CHAR_LIMIT) {
            window.localStorage.removeItem(CHAR_LIMIT_KEY);
            return;
        }
        window.localStorage.setItem(CHAR_LIMIT_KEY, String(
            Math.min(MAX_CHAR_LIMIT, Math.max(MIN_CHAR_LIMIT, n))
        ));
    } catch {
        /* localStorage unavailable / quota — ignore */
    }
}

export default function SteeringGenerator(props) {
    const [urlEntries, setUrlEntries] = useState(
        () => loadRecentUrlEntries() || [{ id: 1, url: '' }]
    );
    const [charLimit, setCharLimit]             = useState(() => loadCharLimit());
    const [isProcessing, setIsProcessing]       = useState(false);
    const [results, setResults]                 = useState([]);       // completed results keyed by jobId
    const [pipelineStages, setPipelineStages]   = useState({});       // jobId -> stage info
    const [streamingDocs, setStreamingDocs]     = useState({});       // jobId -> partial doc text
    const [error, setError]                     = useState(null);
    const [selectedResultIdx, setSelectedResultIdx] = useState(null);
    const [connected, setConnected]             = useState(false);

    // track how many jobs are still in-flight
    const pendingJobsRef = useRef(new Set());
    const socketRef      = useRef(null);

    // ── Socket.io connection ─────────────────────────────────────────────────
    useEffect(() => {
        const socket = io(STEERING_GENERATOR_ENDPOINT, {
            transports:   ['websocket'],
            autoConnect:  true,
            reconnection: true,
        });

        socket.on('connect',       () => { setConnected(true);  setError(null); });
        socket.on('disconnect',    () => setConnected(false));
        socket.on('connect_error', (err) => {
            setError(`Connection error: ${err.message}`);
            setConnected(false);
        });

        // Server confirms which jobs are starting — pre-create placeholders
        socket.on('batch_started', ({ jobs }) => {
            jobs.forEach(({ jobId, url }) => {
                pendingJobsRef.current.add(jobId);
                setPipelineStages(prev => ({
                    ...prev,
                    [jobId]: { stage: 'fetch', iterations: 0, arc_count: 0, title: url },
                }));
                setResults(prev => {
                    // avoid duplicates on reconnect
                    if (prev.find(r => r.jobId === jobId)) return prev;
                    const next = [...prev, {
                        jobId, title: url, url,
                        authors: '', date: '', services: '',
                        doc: null, score: null,
                        iterations: null, arc_count: 0,
                        status: 'pending', error: null,
                    }];
                    // auto-select first card immediately so it's visible
                    if (next.length === 1) setSelectedResultIdx(0);
                    return next;
                });
            });
        });

        socket.on('stage_update', (data) => {
            const { jobId } = data;
            if (!jobId) return;
            setPipelineStages(prev => ({
                ...prev,
                [jobId]: {
                    ...prev[jobId],
                    stage:      data.stage,
                    iterations: data.iterations ?? prev[jobId]?.iterations ?? 0,
                    arc_count:  data.arc_count  ?? prev[jobId]?.arc_count  ?? 0,
                    title:      data.title      || prev[jobId]?.title || '',
                },
            }));
            // Always update metadata fields when present in the event (even if "N/A")
            if ('title' in data || 'authors' in data || 'date' in data || 'services' in data) {
                setResults(prev => prev.map(r => {
                    if (r.jobId !== jobId) return r;
                    return {
                        ...r,
                        title:    data.title    !== undefined ? data.title    : r.title,
                        authors:  data.authors  !== undefined ? data.authors  : r.authors,
                        date:     data.date     !== undefined ? data.date     : r.date,
                        services: data.services !== undefined ? data.services : r.services,
                    };
                }));
            }
        });

        socket.on('doc_chunk', (data) => {
            const { jobId, content } = data;
            if (!jobId) return;
            setStreamingDocs(prev => ({ ...prev, [jobId]: (prev[jobId] || '') + (content || '') }));
        });

        socket.on('result', (data) => {
            const { jobId } = data;
            if (!jobId) return;

            setPipelineStages(prev => ({
                ...prev,
                [jobId]: { ...prev[jobId], stage: data.status === 'success' ? 'done' : 'failed' },
            }));

            setResults(prev => {
                const idx = prev.findIndex(r => r.jobId === jobId);
                const existing = prev[idx] || {};
                const updated = {
                    jobId,
                    title:      data.title       || existing.url || jobId,
                    url:        existing.url      || '',
                    authors:    data.authors      || existing.authors || '',
                    date:       data.date         || existing.date    || '',
                    services:   data.services     || existing.services || '',
                    doc:        data.steering_doc || '',
                    score:      data.final_score  ?? null,
                    iterations: data.iterations   ?? null,
                    arc_count:  data.arc_count    ?? 0,
                    cached:     data.cached === true,
                    cached_stored_at: data.cached_stored_at || null,
                    status:     data.status,
                    error:      data.error        || null,
                };
                const next = idx >= 0
                    ? prev.map((r, i) => i === idx ? updated : r)
                    : [...prev, updated];
                // auto-select first completed result
                if (next.filter(r => r.status !== 'pending').length === 1) {
                    setSelectedResultIdx(next.findIndex(r => r.status !== 'pending'));
                }
                return next;
            });

            // clear streaming buffer for this job
            setStreamingDocs(prev => { const n = { ...prev }; delete n[jobId]; return n; });

            // mark job done
            pendingJobsRef.current.delete(jobId);
            if (pendingJobsRef.current.size === 0) {
                setIsProcessing(false);
            }
        });

        socket.on('error', (data) => {
            const { jobId, message } = data;
            if (jobId) {
                // per-job error — mark that job failed
                setResults(prev => prev.map(r =>
                    r.jobId === jobId ? { ...r, status: 'failed', error: message } : r
                ));
                setPipelineStages(prev => ({
                    ...prev,
                    [jobId]: { ...prev[jobId], stage: 'failed' },
                }));
                setStreamingDocs(prev => { const n = { ...prev }; delete n[jobId]; return n; });
                pendingJobsRef.current.delete(jobId);
                if (pendingJobsRef.current.size === 0) setIsProcessing(false);
            } else {
                // connection-level error
                setError(message || 'An error occurred');
                setIsProcessing(false);
                pendingJobsRef.current.clear();
            }
        });

        socket.on('stopped', () => {
            setIsProcessing(false);
            setStreamingDocs({});
            pendingJobsRef.current.clear();
        });

        socketRef.current = socket;
        sendAnalytics({ demo: demoModule });

        return () => {
            socket.off('connect');
            socket.off('disconnect');
            socket.off('connect_error');
            socket.off('batch_started');
            socket.off('stage_update');
            socket.off('doc_chunk');
            socket.off('result');
            socket.off('error');
            socket.off('stopped');
            socket.disconnect();
        };
    }, []);

    // ── Submit — send all URLs at once ───────────────────────────────────────
    const handleSubmit = useCallback(() => {
        const validEntries = urlEntries.filter(e => e.url.trim());
        if (!validEntries.length) {
            setError('Add at least one URL before generating.');
            return;
        }
        if (!connected) {
            setError('Not connected to server. Please wait and try again.');
            return;
        }

        // Persist the submitted URLs so the user can come back later (after
        // closing the tab) and get the cached result instantly by hitting
        // "Generate" again. The backend pipeline runs to completion even
        // after the websocket disconnects, so the result lands in the
        // server-side DDB cache and re-submission becomes a cache hit.
        saveRecentUrlEntries(validEntries);

        setIsProcessing(true);
        setError(null);
        setResults([]);
        setStreamingDocs({});
        setSelectedResultIdx(null);
        setPipelineStages({});
        pendingJobsRef.current.clear();

        sendAnalytics({ demo: demoModule, action: 'generate', urlCount: validEntries.length, charLimit });

        // Snap blank/transient values back to the default before emitting,
        // so the backend always sees a positive int. The backend also
        // coerces, but doing it here keeps the analytics payload clean.
        const effectiveLimit = (typeof charLimit === 'number' && charLimit >= MIN_CHAR_LIMIT)
            ? charLimit
            : DEFAULT_CHAR_LIMIT;

        socketRef.current.emit('generate_batch', {
            urls:       validEntries.map(e => e.url.trim()),
            char_limit: effectiveLimit,
        });
    }, [urlEntries, connected, charLimit]);

    const handleStop = useCallback(() => {
        socketRef.current?.emit('stop');
        pendingJobsRef.current.clear();
    }, []);

    const handleClear = useCallback(() => {
        handleStop();
        setResults([]);
        setStreamingDocs({});
        setError(null);
        setSelectedResultIdx(null);
        setPipelineStages({});
        setUrlEntries([{ id: 1, url: '' }]);
        // Also clear the persisted URL list - "Clear" is the user explicitly
        // resetting state.
        saveRecentUrlEntries([]);
    }, [handleStop]);

    const hasResults = results.length > 0;

    return (
        <React.Fragment>
            <TopPanel label={demoModule} />
            <AppLayout
                navigation={<ServiceNavigation />}
                content={
                    <SpaceBetween direction="vertical" size="m">
                        <BrowserCheck />

                        <SynopsisPanelV3 label="Synopsis" demotype={demoType} architectureImgs={[]}>
                            Generate steering documents for AWS Edge Services blog posts.
                            Steering docs tell an AI agent <em>when</em> and <em>how</em> to surface a blog post to users.
                            <br /><br />
                            <strong>How it works:</strong> Enter one or more AWS blog URLs. The multi-agent pipeline
                            (Fetch → Draft → Critic → Revise → Judge) processes all URLs <strong>in parallel</strong> and streams each
                            steering document as it's generated.
                            <br /><br />
                            <strong>MCP servers used:</strong>
                            <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                                <li><strong>AWS Knowledge MCP</strong> (<code>knowledge-mcp.global.api.aws</code>) — searches AWS documentation, retrieves SOPs and Well-Architected guidance, and currency-checks the post's claims to populate the Errata, Updates and Overrides and Related content sections</li>
                                <li><strong>AWS Documentation MCP</strong> (<code>awslabs.aws-documentation-mcp-server</code>) — validates every URL in Related content by fetching the live page and confirming it exists and contains expected content</li>
                            </ul>
                            <br />
                            💡 Powered by Amazon Bedrock with Claude Sonnet 4.6. Both MCP servers run via AgentCore Gateway with Cognito JWT auth.
                        </SynopsisPanelV3>

                        {!connected && (
                            <Alert type="warning">Connecting to steering generator service...</Alert>
                        )}

                        {error && (
                            <Alert type="error" dismissible onDismiss={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        <UrlInputPanel
                            urlEntries={urlEntries}
                            setUrlEntries={setUrlEntries}
                            isProcessing={isProcessing}
                            connected={connected}
                            onSubmit={handleSubmit}
                            onStop={handleStop}
                            onClear={handleClear}
                            charLimit={charLimit}
                            setCharLimit={(v) => {
                                setCharLimit(v);
                                saveCharLimit(v);
                            }}
                        />

                        {(isProcessing || Object.keys(pipelineStages).length > 0) && (
                            <PipelineStatus
                                pipelineStages={pipelineStages}
                                streamingDocs={streamingDocs}
                                isProcessing={isProcessing}
                            />
                        )}

                        {hasResults && (
                            <SteeringDocViewer
                                results={results}
                                streamingDocs={streamingDocs}
                                selectedResultIdx={selectedResultIdx}
                                setSelectedResultIdx={setSelectedResultIdx}
                            />
                        )}
                    </SpaceBetween>
                }
                contentType="default"
                navigationOpen={props?.navigationState}
                onNavigationChange={e => props?.setNavigationState?.(e.detail.open)}
            />
        </React.Fragment>
    );
}
